import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { ArticleSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/yihao';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="w-full px-6 md:px-10 pt-24 pb-10">
          <div className="max-w-4xl mx-auto text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);

  return (
    <>
      <ArticleSchema
        title={article.title}
        description={article.excerpt}
        datePublished={article.date}
        imageUrl={article.image}
        url={`https://bukedg.cn/article/${article.id}`}
      />
      <WebPageSchema
        title={`${article.title} - 壹号娱乐`}
        description={article.excerpt}
        url={`https://bukedg.cn/article/${article.id}`}
      />
      <BreadcrumbListSchema />

      <Navbar />
      <Breadcrumb items={[{ label: article.category, to: `/category/live-casino` }, { label: article.title }]} />

      <main>
        <article className="w-full px-6 md:px-10 py-8">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-foreground-400 text-sm">{article.date}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-600 text-base leading-relaxed">{article.excerpt}</p>
            </header>

            <div className="relative h-[300px] md:h-[420px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="text-foreground-800 text-base leading-relaxed space-y-5">
              {article.content.split('\n').filter(Boolean).map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-foreground-500 text-sm">标签：</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors">壹号娱乐</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors">娱乐平台</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors">{article.category}</span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-6 md:px-10 py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer border border-background-200/60 hover:border-primary-200 transition-colors"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-bold text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                        {ra.title}
                      </h3>
                      <span className="text-foreground-400 text-xs">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}